import { useEffect, useMemo, useRef, useState } from 'react'
import { HeaderContext } from '@tanstack/react-table'
import clsx from 'clsx'

import { Button } from '@/ui/Button'
import { Popup } from '@/ui/Popup'
import { generateTimeOptions } from '../../helpers/generateTimeOptions'
import { SuggestionInputTable } from '@/ui/SuggestionInputTable'
import { useDateStore } from '@/components/DateInput'

import styles from './TimeHeader.module.scss'
import commonStyles from './Common.module.scss'

import ArrowDownIcon from '../../assets/arrow-down.svg'
import { filtersIsAfter } from '../../helpers/parseTimeFilter'

type Props<T> = HeaderContext<T, unknown> & {
    label: string
    buttonLabel?: string
    includePreviousDay: boolean
    limitToTime?: Date
}

const timePattern = /^(?:[01]\d|2[0-3]):[0-5]\d$/
const timeWithDayPattern = /^(?:[01]\d|2[0-3]):[0-5]\d \((Mon|Tue|Wed|Thu|Fri|Sat|Sun)\)$/

export const TimeHeader = <T,>({ column, table, label, buttonLabel, includePreviousDay, limitToTime }: Props<T>) => {
    const { date } = useDateStore()

    const [from, setFrom] = useState<string>('')
    const [to, setTo] = useState<string>('')
    const state = table.getState()

    const isActive = useMemo(
        () =>
            state.sorting.find((item) => item.id === column.id) ||
            state.columnFilters.find((item) => item.id === column.id),
        [state]
    )

    const fromOptions = useMemo(
        () => generateTimeOptions(includePreviousDay, limitToTime),
        [includePreviousDay, limitToTime]
    )
    const toOptions = useMemo(
        () =>
            from
                ? fromOptions.filter((item) => {
                      return !filtersIsAfter(item.value, from)
                  })
                : fromOptions,
        [from]
    )

    const withDayOnEnd = useMemo(() => {
        if (fromOptions.length === 0) return false
        return timeWithDayPattern.test(fromOptions[0].value)
    }, [fromOptions])

    const isFromValid = useMemo(
        () => from && (withDayOnEnd ? timeWithDayPattern.test(from) : timePattern.test(from)),
        [from, withDayOnEnd]
    )
    const isToValid = useMemo(
        () => to && (withDayOnEnd ? timeWithDayPattern.test(to) : timePattern.test(to)),
        [to, withDayOnEnd]
    )

    const handleSortColumn = () => {
        column.toggleSorting(true)
    }

    const clearValues = () => {
        setFrom('')
        setTo('')
        column.setFilterValue(undefined)
    }

    const dontResetTo = useRef(false)

    useEffect(() => {
        clearValues()
    }, [date])

    useEffect(() => {
        const filterValue = column.getFilterValue()
        if (filterValue) {
            const theFilterValue = filterValue as { from?: string; to?: string }
            setFrom(theFilterValue.from || '')
            setTo(theFilterValue.to || '')
            dontResetTo.current = true
        }
    }, [])

    useEffect(() => {
        if (dontResetTo.current || to) {
            dontResetTo.current = false
            return
        }
        setTo('')
    }, [from])

    useEffect(() => {
        if (isFromValid || isToValid) {
            column.setFilterValue({ from, to })
        }
    }, [from, to])

    return (
        <Popup
            triggerComponent={(open: boolean) => (
                <Button
                    size="small"
                    variant="transparent"
                    className={clsx(commonStyles.trigger, { [commonStyles.active]: open || isActive })}
                >
                    {label}
                    <ArrowDownIcon />
                </Button>
            )}
            attachToTrigger
            placement="bottom-end"
            className={clsx(commonStyles.popup, styles.popup)}
        >
            <div className={styles.container}>
                {buttonLabel && (
                    <div className={styles.actionWrapper}>
                        <Button variant="transparent" size="small" onClick={handleSortColumn}>
                            {buttonLabel}
                        </Button>
                    </div>
                )}
                <div className={styles.optionsWrapper}>
                    <div className={styles.optionsActions}>
                        <p className={styles.selectedLabel}>Select time</p>
                        <button
                            className={clsx(styles.clear, {
                                [styles.clearActive]: isFromValid || isToValid,
                            })}
                            onClick={clearValues}
                        >
                            Clear
                        </button>
                    </div>
                    <div className={styles.timeContainer}>
                        <div className={styles.timeWrapper}>
                            <p className={styles.timeLabel}>From</p>
                            <SuggestionInputTable
                                options={fromOptions}
                                placeholder="From"
                                value={from}
                                onChange={setFrom}
                                className={styles.timePopup}
                                onlyFromDropdown
                            />
                        </div>
                        <div className={styles.timeWrapper}>
                            <p className={styles.timeLabel}>To</p>
                            <SuggestionInputTable
                                options={toOptions}
                                placeholder="To"
                                value={to}
                                onChange={setTo}
                                className={styles.timePopup}
                                onlyFromDropdown
                            />
                        </div>
                    </div>
                </div>
            </div>
        </Popup>
    )
}
